'use client';

import type { ReactElement, ReactNode } from 'react';
import { Children, cloneElement, Fragment, isValidElement } from 'react';
import { cn } from '@/lib/ui/utils';

interface ModalFooterProps {
  children: ReactNode;
  className?: string;
  /**
   * Toolbar mode for footers with many secondary actions. Renders compact,
   * natural-width buttons that WRAP onto new rows instead of the default
   * equal-width single primary-action row. Use it when more than ~2 buttons
   * would otherwise overflow a phone width (e.g. the manage-wallets modal's
   * Send / Create / Connect / Import). Default stays the one-row spec, so other
   * modals are unaffected.
   */
  wrap?: boolean;
}

// On mobile each child becomes a flex-1 xl-sized button (SilkSheet mobile
// footer spec: one row, equal-width, biggest AnimatedButton size). At sm+
// we revert to natural width and the default size — desktop UI unchanged.
const FOOTER_CHILD_MOBILE_OVERRIDE: string = cn(
  'flex-1 sm:flex-none',
  'h-12 gap-2 rounded-[1rem] text-body-lg',
  'sm:h-9 sm:gap-1.5 sm:rounded-control sm:text-body',
);

// Toolbar mode: compact, natural-width buttons (no flex-1 stretch) at the
// default control size on every breakpoint, so a row of many actions wraps
// instead of overflowing.
const FOOTER_CHILD_WRAP_OVERRIDE: string = cn('h-9 gap-1.5 rounded-control text-body');

export function ModalFooter({
  children,
  className,
  wrap = false,
}: ModalFooterProps): React.ReactNode {
  const childOverride = wrap ? FOOTER_CHILD_WRAP_OVERRIDE : FOOTER_CHILD_MOBILE_OVERRIDE;
  return (
    <div
      className={cn(
        'flex flex-row gap-2 sm:justify-end',
        wrap && 'flex-wrap justify-end',
        className,
      )}
    >
      {styleFooterChildren(children, childOverride)}
    </div>
  );
}

function styleFooterChildren(children: ReactNode, childOverride: string): ReactNode {
  return Children.map(children, (child): ReactNode => styleFooterChild(child, childOverride));
}

function styleFooterChild(child: ReactNode, childOverride: string): ReactNode {
  if (!isValidElement(child)) return child;
  if (child.type === Fragment) {
    const fragment = child as ReactElement<{ children?: ReactNode }>;
    return cloneElement(
      fragment,
      undefined,
      styleFooterChildren(fragment.props.children, childOverride),
    );
  }

  const typed = child as ReactElement<{ className?: string }>;
  return cloneElement(typed, {
    className: cn(childOverride, typed.props.className),
  });
}
